/**
 * The Spec Worker origin, the route segment, launch visibility helpers, and
 * suggested prompts.
 *
 * Deliberately its own module with zero runtime imports. `__root.tsx` has to
 * decide whether to load the chat runtime at all, and the runtime pulls in
 * `@assistant-ui/react` (33 kB gzip on an entry chunk already past vite's
 * warning threshold). Reading this gate from `lib/spec.ts` would drag that
 * module, its client, and its Supabase and API imports into the entry graph to
 * answer a question about one environment variable. `lib/spec.ts` re-exports
 * the worker helpers, so every other caller is unaffected.
 */

/**
 * The path segment Spec's own full-page route occupies, in ONE place.
 *
 * Two different functions answer "is the member on Spec?" from two different
 * inputs, and both are load-bearing: `isSpecSurfaceRoute(matches)` in
 * `spec-context-adapters.ts` decides whether the context store RETAINS the
 * member's page chip, and `isSpecFullPageRoute(pathname)` in
 * `follow-provider.tsx` decides whether the mode is `full_page`. Keeping both
 * duplicates is correct -- route ids and pathnames are genuinely different
 * things here, and a workspace whose slug is literally `spec` is not the Spec
 * route -- but the STRING must not be. A rename that reached only one of them
 * would detach the page chip with a green suite.
 *
 * Lives in this module because it is the one Spec module with no runtime
 * imports, so both a pure lib and a React component can read it without
 * dragging anything else along.
 */
export const SPEC_ROUTE_SEGMENT = 'spec';

export function readSpecWorkerBaseUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return trimmed.replace(/\/+$/, '');
}

export const SPEC_WORKER_BASE_URL: string | null = readSpecWorkerBaseUrl(
  import.meta.env.VITE_SPEC_URL as string | undefined,
);

/**
 * Whether this build can reach a Spec Worker at all.
 *
 * Unset in production on purpose (ledger ruling R7): the route is
 * development-only for now, so an absent variable is the outer of the two
 * switches that keep Spec dark, with the server's `off` launch state inside it.
 */
export function isSpecWorkerConfigured(): boolean {
  return SPEC_WORKER_BASE_URL !== null;
}

/**
 * Local-only web override. Production builds ignore the flag even when the
 * string is present in the environment, matching the Worker's
 * `ENV=development` + `SPEC_LOCAL_OVERRIDE=true` pair.
 */
export function isSpecLocalOverrideEnabled(
  env: {
    PROD?: boolean;
    VITE_SPEC_LOCAL_OVERRIDE?: string | undefined;
  } = import.meta.env,
): boolean {
  if (env.PROD) return false;
  return env.VITE_SPEC_LOCAL_OVERRIDE === 'true';
}

/**
 * Whether Spec may appear in the sidebar and answer its own route.
 *
 * Launch `off` hides everything in production. The local override is the only
 * way to display the surface while the server remains fail-closed.
 */
export function isSpecSurfaceVisible(
  launchState: 'off' | 'read_only' | 'on' | null | undefined,
  options: {
    workerConfigured?: boolean;
    localOverride?: boolean;
  } = {},
): boolean {
  const workerConfigured = options.workerConfigured ?? isSpecWorkerConfigured();
  const localOverride = options.localOverride ?? isSpecLocalOverrideEnabled();
  if (localOverride) return true;
  if (!workerConfigured) return false;
  return launchState === 'on' || launchState === 'read_only';
}

/**
 * Suggested empty-state prompts. Configuration-backed copy only: clicking one
 * inserts the text into the composer. It does not start a run or invent a
 * workflow primitive.
 */
export interface SpecSuggestedPrompt {
  id: string;
  label: string;
  text: string;
}

export const SPEC_SUGGESTED_PROMPTS: readonly SpecSuggestedPrompt[] = [
  {
    id: 'active-projects',
    label: 'Summarize active projects',
    text: 'Summarize the active projects in this workspace and call out anything that looks stalled.',
  },
  {
    id: 'account-overview',
    label: 'Give me an account overview',
    text: 'Give me an overview of the key accounts in this workspace and any open follow-ups.',
  },
  {
    id: 'find-contacts',
    label: 'Find me contacts',
    text: 'Find the contacts I should reach out to next, and say why each one is worth a call.',
  },
];
